// src/game/scenes/GameScene.ts
import Phaser from "phaser";
import { Player } from "../entities/Player";

export class GameScene extends Phaser.Scene {
  private player1!: Player;
  private player2!: Player;
  private platforms!: Phaser.Physics.Arcade.StaticGroup;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: any;
  private background!: Phaser.GameObjects.Image;

  constructor() {
    super({ key: "GameScene" });
  }

  preload(): void {
    // Placeholder sprites are already created in MenuScene
    // Later you'll load real assets here:
    // this.load.spritesheet('player1', 'assets/sprites/player1.png', { frameWidth: 32, frameHeight: 48 });
  }

  create(): void {
    // Add background
    this.background = this.add.image(400, 300, "background");

    // Create platforms group
    this.platforms = this.physics.add.staticGroup();

    // Create level platforms
    this.createLevel();

    // Create players
    this.createPlayers();

    // Set up physics collisions
    this.setupPhysics();

    // Create input handlers
    this.setupInput();

    // Add UI elements
    this.createUI();

    // Add camera effects
    this.setupCamera();
  }

  private createLevel(): void {
    // Ground platforms
    this.platforms.create(400, 568, "platform").setScale(12, 1).refreshBody();

    // Floating platforms
    this.platforms.create(600, 450, "platform").setScale(2, 1).refreshBody();
    this.platforms.create(50, 350, "platform").setScale(2, 1).refreshBody();
    this.platforms.create(750, 320, "platform").setScale(2, 1).refreshBody();
    this.platforms.create(300, 250, "platform").setScale(2, 1).refreshBody();
    this.platforms.create(500, 180, "platform").setScale(2, 1).refreshBody();

    // Create walls
    this.platforms.create(16, 300, "platform").setScale(1, 18).refreshBody(); // Left wall
    this.platforms.create(784, 300, "platform").setScale(1, 18).refreshBody(); // Right wall
  }

  private createPlayers(): void {
    // Create Player 1 (blue, controlled with arrow keys)
    this.player1 = new Player(this, 100, 450, "player1", "player1");

    // Create Player 2 (red, controlled with WASD)
    this.player2 = new Player(this, 150, 450, "player2", "player2");

    // Set different colors/tints to distinguish players
    this.player1.setTint(0x0099ff); // Blue tint
    this.player2.setTint(0xff3300); // Red tint
  }

  private setupPhysics(): void {
    // Player 1 collisions
    this.physics.add.collider(this.player1, this.platforms);
    this.physics.add.collider(this.player1, this.player2); // Players can push each other

    // Player 2 collisions
    this.physics.add.collider(this.player2, this.platforms);
  }

  private setupInput(): void {
    // Arrow keys for Player 1
    this.cursors = this.input.keyboard!.createCursorKeys();

    // WASD keys for Player 2
    this.wasdKeys = this.input.keyboard!.addKeys("W,S,A,D");

    // ESC to return to menu
    this.input.keyboard!.on("keydown-ESC", () => {
      this.scene.start("MenuScene");
    });

    // R to restart level
    this.input.keyboard!.on("keydown-R", () => {
      this.restartLevel();
    });

    // Set up mobile input handling
    this.setupMobileInput();
  }

  private setupMobileInput(): void {
    // Create mobile input state
    this.registry.set("mobileInput", {
      player1: { left: false, right: false, jump: false },
      player2: { left: false, right: false, jump: false },
    });

    // Listen for mobile input events from React
    this.registry.events.on(
      "mobile-input",
      (data: { player: 1 | 2; action: string; pressed: boolean }) => {
        const mobileInput = this.registry.get("mobileInput");
        mobileInput[`player${data.player}`][data.action] = data.pressed;
        this.registry.set("mobileInput", mobileInput);
      }
    );
  }

  private createUI(): void {
    // Player labels
    this.add.text(20, 20, "PLAYER 1 (Blue): Arrow Keys", {
      fontSize: "16px",
      color: "#0099ff",
      backgroundColor: "#000000",
      padding: { x: 10, y: 5 },
    });

    this.add.text(20, 50, "PLAYER 2 (Red): WASD", {
      fontSize: "16px",
      color: "#ff3300",
      backgroundColor: "#000000",
      padding: { x: 10, y: 5 },
    });

    // Controls reminder
    this.add.text(20, 100, "ESC: Menu | R: Restart", {
      fontSize: "14px",
      color: "#ffffff",
      backgroundColor: "#000000",
      padding: { x: 10, y: 5 },
    });

    // Goal text
    this.add
      .text(400, 50, "GOAL: Work together to explore!", {
        fontSize: "20px",
        color: "#ffffff",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 2,
      })
      .setOrigin(0.5);
  }

  private setupCamera(): void {
    // Make camera follow both players
    // Calculate center point between players for camera focus
    // For now, just center the camera
    this.cameras.main.setBounds(0, 0, 800, 600);
  }

  private restartLevel(): void {
    // Reset player positions
    this.player1.resetToSpawn(100, 450);
    this.player2.resetToSpawn(150, 450);

    // Add screen flash effect
    this.cameras.main.flash(200, 255, 255, 255);
  }

  update(): void {
    // Get mobile input state
    const mobileInput = this.registry.get("mobileInput") || {
      player1: { left: false, right: false, jump: false },
      player2: { left: false, right: false, jump: false },
    };

    // Update players with combined keyboard and mobile input
    this.updatePlayerInput(this.player1, this.cursors, mobileInput.player1);
    this.updatePlayerInput(this.player2, this.wasdKeys, mobileInput.player2);

    // Update camera to follow both players (simple version)
    this.updateCamera();

    // Check for fall-off reset
    this.checkBounds();
  }

  private updatePlayerInput(
    player: Player,
    keyboardInput: any,
    mobileInput: any
  ): void {
    // Create a new input object to avoid mutating the original
    const inputState: any = {};

    // Copy keyboard input state
    if (keyboardInput) {
      Object.keys(keyboardInput).forEach((key) => {
        if (
          keyboardInput[key] &&
          typeof keyboardInput[key] === "object" &&
          "isDown" in keyboardInput[key]
        ) {
          inputState[key] = { isDown: keyboardInput[key].isDown };
        }
      });
    }

    // Override with mobile input for specific keys
    if (mobileInput) {
      // For Player 1 (arrow keys)
      if (player === this.player1) {
        if (inputState.left)
          inputState.left.isDown =
            keyboardInput.left?.isDown || !!mobileInput.left;
        if (inputState.right)
          inputState.right.isDown =
            keyboardInput.right?.isDown || !!mobileInput.right;
        if (inputState.up)
          inputState.up.isDown = keyboardInput.up?.isDown || !!mobileInput.jump;
      }

      // For Player 2 (WASD keys)
      if (player === this.player2) {
        if (inputState.A)
          inputState.A.isDown = keyboardInput.A?.isDown || !!mobileInput.left;
        if (inputState.D)
          inputState.D.isDown = keyboardInput.D?.isDown || !!mobileInput.right;
        if (inputState.W)
          inputState.W.isDown = keyboardInput.W?.isDown || !!mobileInput.jump;
      }
    }

    player.update(inputState);
  }

  private updateCamera(): void {
    // Simple camera following logic
    // Calculate midpoint between players
    const midX = (this.player1.x + this.player2.x) / 2;
    const midY = (this.player1.y + this.player2.y) / 2;

    // Smoothly move camera towards midpoint
    const camera = this.cameras.main;
    const lerpFactor = 0.05;

    camera.scrollX = Phaser.Math.Linear(
      camera.scrollX,
      midX - camera.width / 2,
      lerpFactor
    );
    camera.scrollY = Phaser.Math.Linear(
      camera.scrollY,
      midY - camera.height / 2,
      lerpFactor
    );
  }

  private checkBounds(): void {
    // Reset players if they fall off the screen
    if (this.player1.y > 650) {
      this.player1.resetToSpawn(100, 450);
    }

    if (this.player2.y > 650) {
      this.player2.resetToSpawn(150, 450);
    }
  }
}
